import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { X } from "react-feather";
import { toast } from "react-toastify";
import "./subscriptionRenewals.scss";
import {
  getOfflineRenewalCallDecisionsHistory,
  getOfflineRenewals,
  postOfflineRenewalCallDecision,
  type OfflineRenewalRow,
} from "../../../Api/apiUtils";
import { ConfigContext } from "../../../Api/global/ApiContext";
import ClickToCall from "../../../Telephony/ClickToCall";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  SearchInput,
  Select,
  TextInput,
  Toolbar,
  Badge,
  Table,
  Loader,
  EmptyState,
} from "../../../ui";
import type { BadgeTone } from "../../../ui";

type StatusKey = "upcomingRenewal" | "renewalOverdue" | "expired";

type CallDecisionValue =
  | "CALL_PENDING"
  | "EXPECTING_PAYMENT"
  | "RENEWED"
  | "WILL_NOT_RENEW"
  | "BREAK_VACATIONS"
  | "BREAK_MEDICAL_PERSONAL"
  | "BREAK_EXAMS"
  | "BREAK_WORK"
  | "BREAK_BEREAVEMENT"
  | "EXTENDED_COMPENSATIONS"
  | "REQUEST_TRANSFER_CENTRE"
  | "REQUEST_TRANSFER_COURSE"
  | "REQUEST_TRANSFER_ONLINE"
  | "COURSE_COMPLETED"
  | "REFUND";

// Renewal bucket -> chip. These bucket keys aren't in StatusBadge's map yet, so the tone
// is mapped here (expired is neutral there too).
const STATUS_META: Record<StatusKey, { label: string; tone: BadgeTone }> = {
  upcomingRenewal: { label: "Renewal due", tone: "warning" },
  renewalOverdue: { label: "Renewal overdue", tone: "danger" },
  expired: { label: "Expired", tone: "neutral" },
};

const DECISIONS: Array<{ value: CallDecisionValue; label: string }> = [
  { value: "CALL_PENDING", label: "Call Pending" },
  { value: "EXPECTING_PAYMENT", label: "Expecting payment" },
  { value: "RENEWED", label: "Renewed" },
  { value: "WILL_NOT_RENEW", label: "Will Not Renew" },
  { value: "BREAK_VACATIONS", label: "Break - Vacations" },
  { value: "BREAK_MEDICAL_PERSONAL", label: "Break - Medical / Personal reasons" },
  { value: "BREAK_EXAMS", label: "Break - Exams" },
  { value: "BREAK_WORK", label: "Break - Work commitments" },
  { value: "BREAK_BEREAVEMENT", label: "Break - Bereavement" },
  { value: "EXTENDED_COMPENSATIONS", label: "Extended - Compensations pending" },
  { value: "REQUEST_TRANSFER_CENTRE", label: "Request - Transfer to Other centre" },
  { value: "REQUEST_TRANSFER_COURSE", label: "Request - Transfer to Other Course" },
  { value: "REQUEST_TRANSFER_ONLINE", label: "Request - Transfer to Online" },
  { value: "COURSE_COMPLETED", label: "Course Completed" },
  { value: "REFUND", label: "Refund" },
];

function getDecisionLabel(v: string | undefined | null): string {
  const key = String(v ?? "").trim();
  return DECISIONS.find((d) => d.value === key)?.label || key || "—";
}

function formatDateSafe(iso?: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
}

function toIsoMaybe(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v);
  return s.trim() ? s : null;
}

type RenewalRowView = OfflineRenewalRow & {
  statusKey: StatusKey;
};

export default function SubscriptionRenewals() {
  const config = useContext(ConfigContext);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<RenewalRowView[]>([]);
  const [summary, setSummary] = useState<{
    totalStudents: number;
    awaitingCallOutcome: number;
  }>({ totalStudents: 0, awaitingCallOutcome: 0 });

  const [filters, setFilters] = useState<{
    search: string;
    status: "" | StatusKey;
    courseId: "" | string;
    teacherId: "" | string;
    centerId: "" | string;
    decision: "" | CallDecisionValue;
    fromDate: string;
    toDate: string;
  }>({
    search: "",
    status: "",
    courseId: "",
    teacherId: "",
    centerId: "",
    decision: "",
    fromDate: "",
    toDate: "",
  });

  // History modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<
    Array<{
      id?: number | string;
      decision?: string;
      note?: string | null;
      description?: string | null;
      calledByUserId?: number | string;
      calledByName?: string;
      calledAt?: string;
    }>
  >([]);
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [pendingDecisions, setPendingDecisions] = useState<Record<string, CallDecisionValue>>({});
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (filters.centerId) params.centerId = filters.centerId;
      if (filters.courseId) params.courseId = filters.courseId;
      if (filters.teacherId) params.teacherId = filters.teacherId;
      if (filters.status) params.status = filters.status;
      if (filters.decision) params.decision = filters.decision;
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const data = await getOfflineRenewals(params);
      const nextRows: RenewalRowView[] = [];

      const buckets: Array<[StatusKey, keyof typeof data]> = [
        ["upcomingRenewal", "upcomingRenewal"],
        ["renewalOverdue", "renewalOverdue"],
        ["expired", "expired"],
      ];

      for (const [statusKey, key] of buckets) {
        const bucket = (data as any)?.[key];
        const bucketData: OfflineRenewalRow[] = Array.isArray(bucket?.data)
          ? bucket.data
          : [];
        for (const r of bucketData) {
          nextRows.push({ ...(r as any), statusKey });
        }
      }

      // The HTML mock sorts by expiry date ascending
      nextRows.sort((a, b) => {
        const aIso = (a.expiryDate ?? a.accessUntil ?? "") as string;
        const bIso = (b.expiryDate ?? b.accessUntil ?? "") as string;
        return new Date(aIso).getTime() - new Date(bIso).getTime();
      });

      setRows(nextRows);
      setDescriptions((prev) => {
        const next = { ...prev };
        for (const r of nextRows) {
          const id = String((r.enrollmentId as any) ?? (r as any).id ?? "");
          if (!id || next[id] !== undefined) continue;
          next[id] = String(
            (r as any).currentCallDescription ?? (r as any).description ?? (r as any).note ?? ""
          );
        }
        return next;
      });
      setSummary({
        totalStudents: Number((data as any)?.totalStudents ?? nextRows.length ?? 0),
        awaitingCallOutcome: Number((data as any)?.awaitingCallOutcome ?? 0),
      });
    } catch (e: any) {
      console.error("Failed to fetch offline renewals:", e);
      toast.error("Failed to load renewals");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void fetchRenewals();
  }, [fetchRenewals]);

  const uniqueCourses = useMemo(() => {
    const grmpcourses = (config as any)?.grmpcourses;
    if (!Array.isArray(grmpcourses)) return [];

    return grmpcourses
      .filter(
        (course: any) =>
          course?.name &&
          String(course.name).toLowerCase().includes("offline")
      )
      .map((course: any) => ({
        id: String(course.v2Id || course.id || course.courseId || ""),
        label: String(course.name).trim(),
      }))
      .filter((c: { id: string; label: string }) => c.id && c.label);
  }, [config]);

  const uniqueTeachers = useMemo(() => {
    const map = new Map<string, string>(); // teacherId -> label
    for (const r of rows) {
      const id = String((r.teacherId ?? (r as any).teacherUserId ?? "") as any).trim();
      const label = String(r.teacherName ?? r.teacher ?? "").trim();
      if (id && label && !map.has(id)) map.set(id, label);
    }
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const uniqueCentres = useMemo(() => {
    const centres = config?.offlineCentres;
    const list: any[] = Array.isArray(centres) ? centres : [];
    const map = new Map<string, string>();
    for (const centre of list) {
      const id = String(centre?.centerId ?? centre?.centreId ?? "").trim();
      const campus = String(centre?.name || "").trim();
      const labelled = String(
        centre?.centreName || centre?.centerName || centre?.city || ""
      ).trim();
      // Bangalore campus is stored as centreName "Bangalore" but the table/API use "Whitefield"
      const label = /^whitefield$/i.test(campus) ? campus : labelled || campus;
      if (id && label && !map.has(id)) map.set(id, label);
    }
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [config?.offlineCentres]);

  const callDecisionFromRow = (r: RenewalRowView): string => {
    const v =
      (r.currentCallDecision as any) ??
      (r as any).callDecision ??
      (r as any).decision ??
      "PENDING_CALL";
    return String(v ?? "").trim() || "PENDING_CALL";
  };

  const getRow = (r: RenewalRowView) => {
    const enrollmentId =
      (r.enrollmentId as any) ?? (r as any).id ?? (r as any).enrollment ?? "";
    const studentName =
      (r.studentName as any) ?? (r.name as any) ?? (r as any).student ?? "—";
    const studentCredentialId =
      (r.studentCredentialId as any) ??
      (r.credentialId as any) ??
      (r.studentId as any) ??
      "—";
    const courseLevel = r.courseLevel ?? r.course ?? "—";
    const teacherName = r.teacherName ?? r.teacher ?? "—";
    const centreName = r.centerName ?? r.centreName ?? r.center ?? r.centre ?? "—";
    const totalClasses =
      r.totalClasses ?? (r as any).classesPurchased ?? (r as any).classes ?? "—";
    const expiryDate = r.expiryDate ?? r.accessUntil ?? null;

    const studentId =
      (r.studentId as any) ?? (r.credentialId as any) ?? (r.studentCredentialId as any) ?? "";
    const courseName = (r as any).courseName ?? "—";
    const mobileNumber = (r as any).mobileNumber ?? "—";

    return {
      enrollmentId,
      studentName: String(studentName),
      studentCredentialId: String(studentCredentialId),
      studentId: String(studentId),
      courseName: String(courseName),
      mobileNumber: String(mobileNumber),
      courseLevel: String(courseLevel),
      teacherName: String(teacherName),
      centreName: String(centreName),
      totalClasses,
      expiryDateIso: toIsoMaybe(expiryDate),
      currentCallDecision: callDecisionFromRow(r),
      callDecisionUpdatedAt: (r.callDecisionUpdatedAt ?? (r as any).callDecisionUpdatedAt) as
        | string
        | null
        | undefined,
      description: String(
        (r as any).currentCallDescription ?? (r as any).description ?? (r as any).note ?? ""
      ),
    };
  };

  const openHistory = async (enrollmentId: string | number | null | undefined) => {
    if (enrollmentId == null || String(enrollmentId).trim() === "") return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const data = await getOfflineRenewalCallDecisionsHistory(enrollmentId);
      setHistory(data);
    } catch (e: any) {
      console.error("Failed to load call history:", e);
      toast.error("Failed to load call history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const recordCallDecision = async (
    enrollmentId: string | number,
    decision: CallDecisionValue,
    description: string
  ) => {
    const id = String(enrollmentId);
    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await postOfflineRenewalCallDecision(enrollmentId, {
        decision,
        description: description.trim(),
      });
      toast.success("Saved");
      await fetchRenewals();
    } catch (e: any) {
      console.error("Failed to post call decision:", e);
      toast.error("Failed to save call decision");
    } finally {
      setSavingIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <Page className="renewals">
      <PageHeader
        eyebrow="Offline"
        title="Subscription Renewals"
        subtitle="Offline students whose classes are due, overdue or expired. Record each call outcome."
      />

      {/* <p className="sub">
        {summary.totalStudents} of {summary.totalStudents} students &middot;{" "}
        {summary.awaitingCallOutcome} awaiting a call outcome
      </p> */}

      <Card>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                placeholder="Search name, ID, course, teacher or centre"
              />
            )}
          </Field>
          <Field label="Status">
            {(ctl) => (
              <Select
                {...ctl}
                value={filters.status}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    status: (e.target.value as any) || "",
                  }))
                }
                options={[
                  { value: "", label: "All statuses" },
                  { value: "upcomingRenewal", label: "Renewal due" },
                  { value: "renewalOverdue", label: "Renewal overdue" },
                  { value: "expired", label: "Expired" },
                ]}
              />
            )}
          </Field>
          <Field label="Course">
            {(ctl) => (
              <Select
                {...ctl}
                value={filters.courseId}
                onChange={(e) => setFilters((p) => ({ ...p, courseId: e.target.value }))}
                disabled={uniqueCourses.length === 0}
                options={[
                  { value: "", label: "All courses" },
                  ...uniqueCourses.map((c) => ({ value: c.id, label: c.label })),
                ]}
              />
            )}
          </Field>
          <Field label="Teacher">
            {(ctl) => (
              <Select
                {...ctl}
                value={filters.teacherId}
                onChange={(e) => setFilters((p) => ({ ...p, teacherId: e.target.value }))}
                disabled={uniqueTeachers.length === 0}
                options={[
                  { value: "", label: "All teachers" },
                  ...uniqueTeachers.map((t) => ({ value: t.id, label: t.label })),
                ]}
              />
            )}
          </Field>
          <Field label="Centre">
            {(ctl) => (
              <Select
                {...ctl}
                value={filters.centerId}
                onChange={(e) => setFilters((p) => ({ ...p, centerId: e.target.value }))}
                disabled={uniqueCentres.length === 0}
                options={[
                  { value: "", label: "All centres" },
                  ...uniqueCentres.map((c) => ({ value: c.id, label: c.label })),
                ]}
              />
            )}
          </Field>
          <Field label="Renewal decision">
            {(ctl) => (
              <Select
                {...ctl}
                value={filters.decision}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, decision: (e.target.value as any) || "" }))
                }
                disabled={false}
                options={[
                  { value: "", label: "All decisions" },
                  ...DECISIONS.map((d) => ({ value: d.value, label: d.label })),
                ]}
              />
            )}
          </Field>
          <Field label="Expiry from">
            {(ctl) => (
              <div className="renewals__date">
                <TextInput
                  {...ctl}
                  type="date"
                  value={filters.fromDate}
                  max={filters.toDate || undefined}
                  onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))}
                />
                {filters.fromDate ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="renewals__date-clear"
                    icon={<X size={14} aria-hidden />}
                    onClick={() => setFilters((p) => ({ ...p, fromDate: "" }))}
                    aria-label="Clear expiry from date"
                  />
                ) : null}
              </div>
            )}
          </Field>
          <Field label="Expiry to">
            {(ctl) => (
              <div className="renewals__date">
                <TextInput
                  {...ctl}
                  type="date"
                  value={filters.toDate}
                  min={filters.fromDate || undefined}
                  onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))}
                />
                {filters.toDate ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="renewals__date-clear"
                    icon={<X size={14} aria-hidden />}
                    onClick={() => setFilters((p) => ({ ...p, toDate: "" }))}
                    aria-label="Clear expiry to date"
                  />
                ) : null}
              </div>
            )}
          </Field>
        </Toolbar>
      </Card>

      {loading ? (
        <Card>
          <Loader label="Loading..." />
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No students match these filters."
          description="Try clearing a filter or widening the expiry date range."
        />
      ) : (
        <Card flush>
          <Table caption="Subscription renewals" className="renewals__table">
            <thead>
              <tr>
                <th>Student name</th>
                <th>Course name</th>
                <th>Course level</th>
                <th>Mobile</th>
                <th>Expiry date</th>
                <th>Renewal decision</th>
                <th>Call summary</th>
                <th>Status</th>
                <th>Payment link</th>
                <th>Record payment</th>
                <th className="ds-num">Classes purchased</th>
                <th>Centre</th>
                <th>Student ID</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                const row = getRow(r);
                const meta = STATUS_META[r.statusKey];
                const rowId = String(row.enrollmentId ?? "");
                const callDecision =
                  (pendingDecisions[rowId] as CallDecisionValue) ||
                  (row.currentCallDecision as CallDecisionValue);
                const isSaving = Boolean(savingIds[rowId]);

                return (
                  <tr key={String(row.enrollmentId ?? row.studentCredentialId)}>
                    <td>
                      <button
                        type="button"
                        className="renewals__student-link ds-strong"
                        onClick={() => {
                          const url = `${process.env.PUBLIC_URL}/pages/student-profile/${row.studentCredentialId}/${row.studentId}`;
                          window.open(url, "_blank");
                        }}
                      >
                        {row.studentName}
                      </button>
                    </td>
                    <td>{row.courseName}</td>
                    <td>{row.courseLevel}</td>
                    <td>
                      <ClickToCall
                        number={row.mobileNumber || undefined}
                        name={row.studentName}
                        sourceRecordId={row.studentId}
                      />
                    </td>
                    <td className="renewals__nowrap ds-strong">
                      <div>{formatDateSafe(row.expiryDateIso)}</div>
                    </td>
                    <td>
                      <Select
                        size="sm"
                        aria-label={`Renewal decision for ${row.studentName}`}
                        className={
                          callDecision === "WILL_NOT_RENEW"
                            ? "renewals__decision renewals__decision--negative"
                            : "renewals__decision"
                        }
                        value={callDecision}
                        disabled={isSaving}
                        onChange={(e) => {
                          if (!rowId) return;
                          setPendingDecisions((prev) => ({
                            ...prev,
                            [rowId]: e.target.value as CallDecisionValue,
                          }));
                        }}
                        options={DECISIONS.map((d) => ({ value: d.value, label: d.label }))}
                      />
                      {row.callDecisionUpdatedAt ? (
                        <div className="renewals__updated ds-text-subtle" title={row.callDecisionUpdatedAt}>
                          updated {formatDateSafe(row.callDecisionUpdatedAt)}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="renewals__note">
                        <textarea
                          className="ds-input renewals__note-input"
                          aria-label={`Call summary for ${row.studentName}`}
                          rows={2}
                          value={descriptions[rowId] ?? row.description ?? ""}
                          disabled={isSaving}
                          onChange={(e) => {
                            if (!rowId) return;
                            setDescriptions((prev) => ({ ...prev, [rowId]: e.target.value }));
                          }}
                          placeholder="Add call summary"
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!rowId || isSaving}
                          onClick={() => {
                            if (!row.enrollmentId) return;
                            void recordCallDecision(
                              row.enrollmentId,
                              callDecision,
                              descriptions[rowId] ?? row.description ?? ""
                            );
                          }}
                        >
                          {isSaving ? "Saving" : "Save"}
                        </Button>
                      </div>
                    </td>
                    <td>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="renewals__nowrap"
                        onClick={() => {
                          const url = `${process.env.PUBLIC_URL}/pages/student-profile/${row.studentCredentialId}/${row.studentId}?tab=Payment+Link`;
                          window.open(url, "_blank");
                        }}
                      >
                        Generate payment link
                      </Button>
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="renewals__nowrap"
                        onClick={() => {
                          const url = `${process.env.PUBLIC_URL}/pages/student-profile/${row.studentCredentialId}/${row.studentId}?tab=Record+Payment`;
                          window.open(url, "_blank");
                        }}
                      >
                        Record payment
                      </Button>
                    </td>
                    <td className="ds-num">{row.totalClasses ?? "—"}</td>
                    <td>{row.centreName}</td>
                    <td className="ds-text-subtle">{row.studentCredentialId}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <Modal isOpen={historyOpen} toggle={() => setHistoryOpen(false)} size="lg" centered className="ds-dialog">
        <h2 className="ds-dialog__title">Call decision history</h2>
        {historyLoading ? (
          <Loader label="Loading history..." />
        ) : history.length === 0 ? (
          <EmptyState title="No call decisions recorded yet." />
        ) : (
          <Table caption="Call decision history">
            <thead>
              <tr>
                <th>Decision</th>
                <th>Note</th>
                <th>Called by</th>
                <th>Called at</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={String(h.id ?? h.calledAt ?? Math.random())}>
                  <td>{getDecisionLabel(h.decision ?? "")}</td>
                  <td>{h.description || h.note || "—"}</td>
                  <td>{h.calledByName ?? "—"}</td>
                  <td>{formatDateSafe(h.calledAt ?? "")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setHistoryOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </Page>
  );
}
